import React from 'react';
import { View, Image, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS as ADMIN_SVG_COMPONENTS } from '@/assets/admin';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

export type AttachmentItem = {
  id: string;
  name: string;
  uri: string;
  type: string;
};

type Props = {
  attachments: AttachmentItem[];
  onRemove: (id: string) => void;
  showDocChip?: boolean;
};

export const AttachmentPreview: React.FC<Props> = ({ attachments, onRemove, showDocChip = true }) => {
  const { colors } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  if (!attachments || attachments.length === 0) return null;
  return (
    <View style={styles.fileUploadPreview}>
      <View style={styles.attachmentsContainer}>
        {attachments.map((f) => {
          const isImage = (f.type || '').startsWith('image/');
          if (isImage) {
            return (
              <View key={f.id} style={styles.imageThumbWrapper}>
                <Image source={{ uri: f.uri }} style={styles.imageThumb} />
                <TouchableOpacity style={styles.imageThumbClose} onPress={() => onRemove(f.id)}>
                  <Text style={styles.imageThumbCloseText}>×</Text>
                </TouchableOpacity>
              </View>
            );
          }
          if (!showDocChip) return null;
          return (
            <View key={f.id} style={styles.docChip}>
              <ADMIN_SVG_COMPONENTS.PAPERCLIP width={14} height={14} color={colors.text.secondary} />
              <Text style={styles.docName} numberOfLines={1}>{f.name}</Text>
              <TouchableOpacity style={styles.docRemove} onPress={() => onRemove(f.id)}>
                <ADMIN_SVG_COMPONENTS.X_CIRCLE width={18} height={18} color={colors.text.tertiary} />
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  fileUploadPreview: { paddingHorizontal: 12, paddingTop: 8 },
  attachmentsContainer: { flexDirection: 'row', flexWrap: 'wrap' },
  imageThumbWrapper: { width: 80, height: 80, borderRadius: 8, overflow: 'hidden', marginRight: 8, marginBottom: 8, backgroundColor: colors.bg.base },
  imageThumb: { width: '100%', height: '100%' },
  imageThumbClose: { position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: 10, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center' },
  imageThumbCloseText: { color: '#FFFFFF', fontSize: 12, fontWeight: '700' },
  docChip: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: semantic.border.secondary, backgroundColor: colors.card.bg, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 16, marginRight: 8, marginBottom: 8 },
  docName: { color: colors.text.primary, maxWidth: 160 },
  docRemove: { marginLeft: 8 },
});

export default AttachmentPreview;


